import { useEffect, useRef, useState } from 'react';

interface UseStablePdfUrlResult {
  activeUrl: string;
  pdfKey: number;
  onLoadSuccess: () => void;
  onLoadError: () => void;
  errorRetryCountRef: React.MutableRefObject<number>;
}

export function useStablePdfUrl(
  documentUrl: string | null | undefined
): UseStablePdfUrlResult {
  const initialUrl = documentUrl ?? '';
  const activeUrlRef = useRef<string>(initialUrl);
  const hasLoadedSuccessfullyRef = useRef<boolean>(false);
  const errorRetryCountRef = useRef<number>(0);
  const [pdfKey, setPdfKey] = useState<number>(0);
  const [activeUrl, setActiveUrl] = useState<string>(initialUrl);

  useEffect(() => {
    if (!documentUrl) {
      return;
    }

    const newBaseUrl = documentUrl.split('?')[0];
    const currentBaseUrl = activeUrlRef.current.split('?')[0];

    if (newBaseUrl !== currentBaseUrl) {
      errorRetryCountRef.current = 0;
      hasLoadedSuccessfullyRef.current = false;
      activeUrlRef.current = documentUrl;
      setActiveUrl(documentUrl);
      setPdfKey((prev) => prev + 1);
      return;
    }

    if (
      !hasLoadedSuccessfullyRef.current &&
      documentUrl !== activeUrlRef.current
    ) {
      activeUrlRef.current = documentUrl;
      setActiveUrl(documentUrl);
      setPdfKey((prev) => prev + 1);
    }
  }, [documentUrl]);

  const onLoadSuccess = () => {
    hasLoadedSuccessfullyRef.current = true;
    errorRetryCountRef.current = 0;
  };

  const onLoadError = () => {
    hasLoadedSuccessfullyRef.current = false;
    errorRetryCountRef.current++;
  };

  return {
    activeUrl,
    pdfKey,
    onLoadSuccess,
    onLoadError,
    errorRetryCountRef
  };
}
